import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatLegacyCard as MatCard } from '@angular/material/legacy-card';
import { MatLegacyPaginator as MatPaginator, LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute } from '@angular/router';
import { NotificationHandlerComponent, StorageService } from '@ebootis/utilities';
import { ConfigurationService } from 'projects/zbs-app/src/app/components/configuration-select/configuration.service';
import { ErrorCodesI18n } from 'projects/zbs-app/src/app/i18n/ErrorCodesI18n';
import { ArticleSearch } from 'projects/zbs-app/src/app/services/Ebootis.Queries/ArticleSearch';
import { ArticleBasketService } from 'projects/zbs-app/src/app/services/article-basket.service';
import { Subject, interval, of } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { ArticleService } from '../../article.service';
import { ArticleSearchService } from '../article-search/article-search.service';
import { ArticleTableI18n } from './ArticleTableI18n';

@Component({
	selector: 'app-article-table',
	templateUrl: './article-table.component.html',
	styleUrls: ['./article-table.component.scss'],
	providers: [ErrorCodesI18n],
})
export class ArticleTableComponent implements OnInit, OnDestroy {

	@Input() set showSelectButton(p_value: boolean) {
		this.m_showSelectBtn = p_value;
		if (this.m_showSelectBtn) {
			this.m_displayedColumns.unshift('select');
		}
	}

	@Input() set onlyOrderableArticles(p_onlyOrderableArticles: boolean) {
		this.m_onlyOrderableArticles = p_onlyOrderableArticles;
	}

	@Input() set articleList(p_value: ArticleSearch.Result) {
		this.m_articleList = p_value;
		//workaround for scrolling back to anchor
		interval(500).pipe(take(1)).pipe(switchMap(() => of(this.scrollToSelectedArticle(this.m_selectedArticleOid)))).subscribe();
	}

	@Input() set pageIndex(p_value: number) {
		this.m_pageIndex = p_value;
	}

	@Input() set sortAttribute(p_value: string) {
		this.m_sortAttribute = p_value;
	}


	@Input() set previouslySelectedArticle(p_articleOid: string) {
		if (p_articleOid && p_articleOid != '') {
			this.m_selectedArticleOid = p_articleOid;
			this.scrollToSelectedArticle(p_articleOid);
		}
	}


	@Output() public articleSelected: EventEmitter<string> = new EventEmitter();
	@Output() public openDetails: EventEmitter<string> = new EventEmitter();
	@Output() public paginatorPageEvent: EventEmitter<PageEvent> = new EventEmitter();
	@Output() public sortingChanged: EventEmitter<string> = new EventEmitter();

	private m_articleCompareList: string[] = [];
	private m_selectedConfigOid: string;
	private m_selectedArticleOid: string;
	m_onlyAvailableStock: boolean;
	private m_subscriptionsSubject = new Subject();
	public readonly viewTypeEnum = ViewType;

	@ViewChild('articleListRef', { static: true }) myAnchor: ElementRef<HTMLElement>;
	@ViewChild('matCard', { static: true }) myMatCard: ElementRef<MatCard>;
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
	public m_sortAttribute: string = '';
	private m_viewType: string;
	public get ViewType() {
		if (!this.m_viewType)
			this.m_viewType = this.storageService.getItem('session', 'ARTICLE_LIST_VIEW_TYPE');
		if (!this.m_viewType)
			this.m_viewType = 'list';
		return this.m_viewType
	}
	public set ViewType(p_value: string) {
		this.m_viewType = p_value;
		this.storageService.setItem('session', 'ARTICLE_LIST_VIEW_TYPE', this.m_viewType);
	}
	public m_articleList: ArticleSearch.Result;
	public m_displayMessage: string;
	public m_pageIndex: number = 0;
	public articleListId: string = 'articleList';
	public m_messageHandlerClass: string = 'info';
	public m_showSelectBtn: boolean = false;
	public m_onlyOrderableArticles: boolean = false;
	public m_displayedColumns: string[] = ['image', 'id', 'short', 'long', 'tool'];
	public m_paginatorPageSize: number = this.m_articleSearchService.getLastSearchParams().limit;
	public m_showStoragePackaging: boolean = false;
	public m_showSubPackaging: boolean = false;
	public m_showStockLevel: boolean = false;

	/** #11308 */
	public m_showSalesPrice: boolean = false;
	public m_showAvailableStock: boolean = false;

	private readonly PAGE_IDX: string = 'pageIndex';

	constructor(
		private m_changeDetectorRef: ChangeDetectorRef,
		private m_articleService: ArticleService,
		private m_basketService: ArticleBasketService,
		private m_configurationService: ConfigurationService,
		private m_snackBar: MatSnackBar,
		private m_translation: ArticleTableI18n,
		private storageService: StorageService,
		public m_articleSearchService: ArticleSearchService,
		private route: ActivatedRoute,

	) {
	}

	/**
	 * Lädt die Artikelvergleichsliste sowie die Konfigurations OID.
	 */
	public ngOnInit(): void {
		this.m_articleCompareList = this.m_articleService.getArticleCompare();

		this.m_selectedConfigOid = this.m_configurationService.SelectedConfigOid;
		this.m_configurationService.getConfiguration().pipe(takeUntil(this.m_subscriptionsSubject)).subscribe({
			next: (config) => {
				this.m_showStoragePackaging = config?.showStoragePackaging;
				this.m_showSubPackaging = config?.showSubPackaging;
				this.m_onlyAvailableStock = config?.onlyAvailableStock;
				this.m_showSalesPrice = config?.showSalesPrice;
				this.m_showAvailableStock = config?.showAvailableStock;
			}
		});

		this.route.fragment.subscribe({
			next: (articleOid: string) => {
				this.previouslySelectedArticle = articleOid;
			}
		});
	}

	private scrollToSelectedArticle(articleOid: string): void {
		if (articleOid) {
			var selectedArticle = document.getElementById(articleOid);
			if (selectedArticle) {
				selectedArticle.scrollIntoView({ behavior: "smooth" });
			}
		}
	}

	/**
	 * Die Subscriptions werden geschlossen.
	 */
	public ngOnDestroy(): void {
		this.m_subscriptionsSubject.next(null);
		this.m_subscriptionsSubject.complete();
	}

	/**
	 * Gibt zurück, ob der Artikel über den
	 * "Auswählen"-Button auswählbar sein soll.
	 */
	public canArticleGetSelected(p_article: ArticleSearch.Article): boolean {
		let canArticleGetSelected = false;
		if (this.m_showSelectBtn) {
			if (this.m_onlyOrderableArticles === true) {
				canArticleGetSelected = this.m_articleService.canArticleGetAddedToBasket(this.m_onlyAvailableStock, p_article);
			} else {
				// alle Artikel anzeigen
				canArticleGetSelected = true;
			}
		}
		return canArticleGetSelected;
	}

	/**
	 * Prüft, ob der Artikel in den Einkaufswagen
	 * gelegt werden kann.
	 */
	public canArticleGetAddedToBasket(p_article: ArticleSearch.Article): boolean {
		return !this.m_showSelectBtn && this.m_articleService.canArticleGetAddedToBasket(this.m_onlyAvailableStock, p_article);
	}

	/**
	 * Die Artikeldetails werden aufgerufen. Die Tooltipps werden entfernt.
	 * @param p_articleOid Artikel OID
	 */
	public articleDetails_Click(p_articleOid: string): void {
		this.openDetails.emit(p_articleOid);
		while (document.getElementsByTagName('mat-tooltip-component').length > 0) {
			document.getElementsByTagName('mat-tooltip-component')[0].remove();
		}
	}

	/**
	 * Fügt einen Artikel für die Artikelsuche hinzu, falls größe des Artikelvergleichs unter 5.
	 * @param p_element Artikel aus der Artikelsuche
	 */
	public articleCompare_Click(p_element: ArticleSearch.Article): void {
		this.m_articleService.addArticleCompare(p_element.oid);
	}

	/**
	 * Fügt einen Artikel auf Basis des angebenen Indexes in den Warenkorb. Im Fehlerfall gibt die aufgrufene Methode einen Fehlercode zurück.
	 * @param p_element Artikel aus der Artikelsuche
	 */
	public async articleAddToBasket_Click(p_element: ArticleSearch.Article): Promise<void> {
		await this.m_basketService.saveBasketItem(p_element.oid, this.m_selectedConfigOid, p_element.salesAmountMin)
			.then(() => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: this.m_translation.globalBasketAddItemSuccess,
						type: 'success',
					},
					duration: 2000,
				});
			})
			.catch((error: Error) => {
				this.m_snackBar.openFromComponent(NotificationHandlerComponent, {
					data: {
						message: error.message,
						type: 'danger',
					},
					duration: 2000,
				});
			});
	}

	/**
	 * Übergibt einen Artikel an die aufrufende Komponente.
	 * @param p_selectedArticleOid ArtikelOid der Artikelsuche
	 */
	public selectArticle_Click(p_selectedArticleOid: string): void {
		this.articleSelected.emit(p_selectedArticleOid);
	}

	/**
	 * Wenn die Seitengröße geändert wurde wird diese an die aufrufende Komponente übergeben.
	 * @param p_pageEvent Event des Paginators
	 */
	public paginator_PAGE(p_pageEvent: PageEvent): void {
		this.paginatorPageEvent.emit(p_pageEvent);
		this.m_pageIndex = p_pageEvent.pageIndex;
		if (p_pageEvent.pageSize != this.m_paginatorPageSize) {
			this.m_paginatorPageSize = p_pageEvent.pageSize;
		}

		try {
			if (this.articleListId) {
				document.querySelector('#' + this.articleListId).scrollIntoView();
			}
		} catch (e) { }
	}

	/**
	 * Die URL für einen Artikelthumb wird zurückgegeben.
	 * @param p_oid Artikel OID
	 */
	public imageUrl(p_oid: string): string {
		return this.m_articleService.getArticleThumbUrl(p_oid);
	}

	/**
	 * Die Nachrichten des MessageHandlers werden zurückgesetzt.
	 */
	public clearDisplayMessages(): void {
		this.m_displayMessage = null;
	}

	/**
	 * Eine Änderung der Sortierung wird an die aufrufende Komponente übergeben.
	 */
	public changeSorting() {
		this.sortingChanged.emit(this.m_sortAttribute);
	}

}

enum SortOrder {
	upward,
	downward,
}

enum ViewType {
	list,
	grid,
}
